import { AbstractConnector } from '@web3-react/abstract-connector'
import { UnsupportedChainIdError } from '@web3-react/core'
import { WalletConnectConnector } from '@web3-react/walletconnect-connector'
import React, { useEffect, useState } from 'react'
import ReactGA from 'react-ga'
import styled from 'styled-components'
import MetamaskIcon from '../../assets/images/metamask.png'
import BitgetIcon from '../../assets/images/bitget.png'
import RabbyIcon from '../../assets/images/rabby.svg'

import { ReactComponent as Close } from '../../assets/images/x.svg'
import { fortmatic, injected } from '../../connectors'
import { OVERLAY_READY } from '../../connectors/Fortmatic'
import { SUPPORTED_WALLETS } from '../../constants'
import usePrevious from '../../hooks/usePrevious'
import { ApplicationModal } from '../../state/application/actions'
import { useModalOpen, useWalletModalToggle } from '../../state/application/hooks'
import AccountDetails from '../AccountDetails'

import Modal from '../Modal'
import Option from './Option'
import PendingView from './PendingView'
import { CHAINS, CURRENT_CHAIN } from '@hyperswap/addresses'
import SwitchNetworkButton from './SwitchNetworkButton'
import { useActiveWeb3React } from 'hooks'

const CloseIcon = styled.div`
  position: absolute;
  right: 1rem;
  top: 14px;
  &:hover {
    cursor: pointer;
    opacity: 0.6;
  }
`

const CloseColor = styled(Close)`
  path {
    stroke: ${({ theme }) => theme.text4};
  }
`

const Wrapper = styled.div`
  ${({ theme }) => theme.flexColumnNoWrap}
  margin: 0;
  padding: 0;
  width: 100%;
`

const HeaderRow = styled.div`
  ${({ theme }) => theme.flexRowNoWrap};
  padding: 1rem 1rem;
  font-weight: 500;
  color: ${(props) => (props.color === 'blue' ? ({ theme }) => theme.primary1 : 'inherit')};
  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 1rem;
  `};
`

const ContentWrapper = styled.div`
  background-color: ${({ theme }) => theme.bg0};
  padding: 0 1rem 1rem 1rem;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;

  ${({ theme }) => theme.mediaWidth.upToMedium`padding: 0 1rem 1rem 1rem`};
`

const UpperSection = styled.div`
  position: relative;

  h5 {
    margin: 0;
    margin-bottom: 0.5rem;
    font-size: 1rem;
    font-weight: 400;
  }

  h5:last-child {
    margin-bottom: 0px;
  }

  h4 {
    margin-top: 0;
    font-weight: 500;
  }
`

const OptionGrid = styled.div`
  display: grid;
  grid-gap: 10px;
  ${({ theme }) => theme.mediaWidth.upToMedium`
    grid-template-columns: 1fr;
    grid-gap: 10px;
  `};
`

const HoverText = styled.div`
  text-decoration: none;
  color: ${({ theme }) => theme.text1};
  display: flex;
  align-items: center;

  :hover {
    cursor: pointer;
  }
`

export const WALLET_VIEWS = {
  OPTIONS: 'options',
  OPTIONS_SECONDARY: 'options_secondary',
  ACCOUNT: 'account',
  PENDING: 'pending',
}

export default function WalletModal({
  pendingTransactions,
  confirmedTransactions,
  ENSName,
}: {
  pendingTransactions: string[] // hashes of pending
  confirmedTransactions: string[] // hashes of confirmed
  ENSName?: string
}) {
  // important that these are destructed from the account-specific web3-react context
  const { active, account, connector, chainId, activate, error } = useActiveWeb3React()

  const [walletView, setWalletView] = useState(WALLET_VIEWS.ACCOUNT)

  const [pendingWallet, setPendingWallet] = useState<AbstractConnector | undefined>()

  const [pendingError, setPendingError] = useState<boolean>()

  const walletModalOpen = useModalOpen(ApplicationModal.WALLET)
  const toggleWalletModal = useWalletModalToggle()

  const previousAccount = usePrevious(account)

  // close on connection, when logged out before
  useEffect(() => {
    if (account && !previousAccount && walletModalOpen) {
      toggleWalletModal()
    }
  }, [account, previousAccount, toggleWalletModal, walletModalOpen])

  // always reset to account view
  useEffect(() => {
    if (walletModalOpen) {
      setPendingError(false)
      setWalletView(WALLET_VIEWS.ACCOUNT)
    }
  }, [walletModalOpen])

  // close modal when a connection is successful
  const activePrevious = usePrevious(active)
  const connectorPrevious = usePrevious(connector)
  useEffect(() => {
    if (walletModalOpen && ((active && !activePrevious) || (connector && connector !== connectorPrevious && !error))) {
      setWalletView(WALLET_VIEWS.ACCOUNT)
    }
  }, [setWalletView, active, error, connector, walletModalOpen, activePrevious, connectorPrevious])

  const tryActivation = async (connector: AbstractConnector | undefined, connectorName = '') => {
    let name = ''
    Object.keys(SUPPORTED_WALLETS).map((key) => {
      if (connector === SUPPORTED_WALLETS[key].connector) {
        return (name = SUPPORTED_WALLETS[key].name)
      }
      return true
    })
    // log selected wallet
    ReactGA.event({
      category: 'Wallet',
      action: 'Change Wallet',
      label: name,
    })
    setPendingWallet(connector) // set wallet for pending view
    setWalletView(WALLET_VIEWS.PENDING)

    // if the connector is walletconnect and the user has already tried to connect, manually reset the connector
    if (connector instanceof WalletConnectConnector && connector.walletConnectProvider?.wc?.uri) {
      connector.walletConnectProvider = undefined
    }

    // Keep in memory to init app on new session with an activate function
    if (connectorName.length > 0) {
      localStorage.setItem('connector', connectorName)
    }

    connector &&
      activate(connector, undefined, true).catch((error) => {
        if (error instanceof UnsupportedChainIdError) {
          activate(connector) // a little janky...can't use setError because the connector isn't set
        } else {
          setPendingError(true)
        }
      })
  }

  // close wallet modal if fortmatic modal is active
  useEffect(() => {
    fortmatic.on(OVERLAY_READY, () => {
      toggleWalletModal()
    })
  }, [toggleWalletModal])

  // get wallets user can switch too, depending on device/browser
  function getOptions() {
    // const filteredWallets = detectedWallet ? { [detectedWallet]: SUPPORTED_WALLETS[detectedWallet] } : SUPPORTED_WALLETS
    const isBitget =
      (window?.bitkeep && window?.bitkeep.ethereum) || SUPPORTED_WALLETS?.['BITGET']?.connector !== injected
    const isMetamask = SUPPORTED_WALLETS?.['METAMASK']?.connector !== injected
    const isRabby = SUPPORTED_WALLETS?.['RABBY']?.connector !== injected

    if (!window.ethereum) {
      return (
        <>
          <Option
            id={`install bitget`}
            key={1}
            color={'#E8831D'}
            header={'Install Bitget Wallet'}
            subheader={'Ultimate Web3 multi-chain wallet.'}
            link={'https://web3.bitget.com/'}
            icon={BitgetIcon}
          />
          <Option
            id={`install metamask`}
            key={2}
            color={'#E8831D'}
            header={'Install Metamask'}
            subheader={'Easy-to-use browser extension.'}
            link={'https://metamask.io/'}
            icon={MetamaskIcon}
          />
          <Option
            id={`install rabby`}
            key={3}
            color={'#E8831D'}
            header={'Install Rabby'}
            subheader={'The game-changing wallet.'}
            link={'https://rabby.io/'}
            icon={RabbyIcon}
          />
        </>
      )
    } else {
      return Object.keys(SUPPORTED_WALLETS).map((key) => {
        const option = SUPPORTED_WALLETS[key]

        if (option.name === 'MetaMask') {
          if (isMetamask && option.connector !== injected) {
            return (
              <Option
                id={`connect-${key}`}
                onClick={() => {
                  option.connector === connector
                    ? setWalletView(WALLET_VIEWS.ACCOUNT)
                    : !option.href && tryActivation(option.connector, key)
                }}
                key={key}
                active={option.connector === connector}
                color={option.color}
                link={option.href}
                header={option.name}
                subheader={option.description}
                icon={option.iconURL}
              />
            )
          } else {
            return (
              <Option
                id={`install metamask`}
                key={2}
                color={'#E8831D'}
                header={'Install Metamask'}
                subheader={'Easy-to-use browser extension.'}
                link={'https://metamask.io/'}
                icon={MetamaskIcon}
              />
            )
          }
        } else if (option.name === 'Rabby') {
          if (isRabby) {
            return (
              <Option
                id={`connect-${key}`}
                onClick={() => {
                  option.connector === connector
                    ? setWalletView(WALLET_VIEWS.ACCOUNT)
                    : !option.href && tryActivation(option.connector, key)
                }}
                key={key}
                active={option.connector === connector}
                color={option.color}
                link={option.href}
                header={option.name}
                subheader={option.description}
                icon={option.iconURL}
              />
            )
          } else {
            return (
              <Option
                id={`install rabby`}
                key={3}
                color={'#E8831D'}
                header={'Install Rabby'}
                subheader={'The game-changing wallet.'}
                link={'https://rabby.io/'}
                icon={RabbyIcon}
              />
            )
          }
        } else if (option.name === 'Bitget Wallet') {
          if (isBitget) {
            return (
              <Option
                id={`connect-${key}`}
                onClick={() => {
                  option.connector === connector
                    ? setWalletView(WALLET_VIEWS.ACCOUNT)
                    : !option.href && tryActivation(option.connector, key)
                }}
                key={key}
                active={option.connector === connector}
                color={option.color}
                link={option.href}
                header={option.name}
                subheader={option.description}
                icon={option.iconURL}
              />
            )
          } else {
            return (
              <Option
                id={`install bitget`}
                key={1}
                color={'#E8831D'}
                header={'Install Bitget Wallet'}
                subheader={'Ultimate Web3 multi-chain wallet.'}
                link={'https://web3.bitget.com/'}
                icon={BitgetIcon}
              />
            )
          }
        }
        // likewise for generic
        else if (option.name === 'Browser Wallet') {
          return (
            <Option
              id={`connect-${key}`}
              onClick={() => {
                option.connector === connector
                  ? setWalletView(WALLET_VIEWS.ACCOUNT)
                  : !option.href && tryActivation(option.connector, key)
              }}
              key={key}
              active={option.connector === connector}
              color={option.color}
              link={option.href}
              header={option.name}
              subheader={option.description}
              icon={option.iconURL}
            />
          )
        }
        return null
      })
    }
  }

  function getModalContent() {
    const wrongNetwork = chainId && chainId?.toString() !== CURRENT_CHAIN.toString()

    if (error || wrongNetwork) {
      return (
        <UpperSection>
          <CloseIcon onClick={toggleWalletModal}>
            <CloseColor />
          </CloseIcon>
          <HeaderRow>{error instanceof UnsupportedChainIdError ? 'Wrong Network' : 'Error connecting'}</HeaderRow>
          <ContentWrapper>
            {error instanceof UnsupportedChainIdError || wrongNetwork ? (
              <>
                <h5>Please connect to the {CHAINS[CURRENT_CHAIN].chainName} network.</h5>
                <SwitchNetworkButton />
              </>
            ) : (
              'Error connecting. Try refreshing the page.'
            )}
          </ContentWrapper>
        </UpperSection>
      )
    }
    if (account && walletView === WALLET_VIEWS.ACCOUNT) {
      return (
        <AccountDetails
          toggleWalletModal={toggleWalletModal}
          pendingTransactions={pendingTransactions}
          confirmedTransactions={confirmedTransactions}
          ENSName={ENSName}
          openOptions={() => setWalletView(WALLET_VIEWS.OPTIONS)}
        />
      )
    }

    return (
      <UpperSection>
        <CloseIcon onClick={toggleWalletModal}>
          <CloseColor />
        </CloseIcon>
        {walletView !== WALLET_VIEWS.ACCOUNT ? (
          <HeaderRow color="blue">
            <HoverText
              onClick={() => {
                setPendingError(false)
                setWalletView(WALLET_VIEWS.ACCOUNT)
              }}
            >
              Back
            </HoverText>
          </HeaderRow>
        ) : (
          <HeaderRow>
            <HoverText>Connect to a wallet</HoverText>
          </HeaderRow>
        )}

        <ContentWrapper>
          {walletView === WALLET_VIEWS.PENDING ? (
            <PendingView
              connector={pendingWallet}
              error={pendingError}
              setPendingError={setPendingError}
              tryActivation={tryActivation}
            />
          ) : (
            <OptionGrid>{getOptions()}</OptionGrid>
          )}
        </ContentWrapper>
      </UpperSection>
    )
  }

  return (
    <Modal isOpen={walletModalOpen} onDismiss={toggleWalletModal} minHeight={false} maxHeight={90}>
      <Wrapper>{getModalContent()}</Wrapper>
    </Modal>
  )
}
